import React, { useCallback, useEffect, useState } from "react";
import { Route, ChevronRight, Lock, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

const nodeCls = (s) => {
  if (s.status === "Passed") return "border-emerald-300 bg-emerald-50 text-emerald-700";
  if (s.is_bottleneck) return "border-red-400 bg-red-50 text-red-700 ring-2 ring-red-200";
  if (s.downstream_at_risk) return "border-amber-300 bg-amber-50 text-amber-700";
  return "border-zinc-300 bg-white text-zinc-600";
};

export default function CriticalPath({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/critical-path`); setData(data); }
    catch { /* non-blocking */ }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const steps = data?.steps || [];
  const blocked = data?.first_blocker;
  return (
    <div data-testid="critical-path" className="border border-zinc-200 rounded-lg p-4 lg:col-span-2">
      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] inline-flex items-center gap-1 mb-2"><Route size={13} /> Delivery critical path</div>
      {loading ? <div className="text-sm text-zinc-400 py-4 text-center">Loading…</div> : steps.length === 0 ? (
        <div className="text-[12px] text-zinc-500 py-4 text-center">No stage-gates yet — add gates to see the critical path.</div>
      ) : (
        <>
          <div data-testid="cp-headline" className={`text-[13px] rounded-md px-3 py-2 mb-4 border ${blocked ? "text-red-700 bg-red-50 border-red-200" : "text-emerald-700 bg-emerald-50 border-emerald-200"}`}>
            {blocked ? <AlertTriangle size={13} className="inline mr-1" /> : <CheckCircle2 size={13} className="inline mr-1" />}
            {data.headline}
          </div>
          <div className="flex items-stretch gap-1 overflow-x-auto pb-2">
            {steps.map((s, i) => (
              <React.Fragment key={s.ref}>
                <div data-testid={`cp-step-${s.ref}`} className="flex-1 min-w-[150px]">
                  <div className={`rounded-lg border px-3 py-2 h-full ${nodeCls(s)}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] tracking-[0.14em] uppercase opacity-70">{s.horizon ? `${s.horizon}d` : s.ref}</span>
                      {s.is_bottleneck && <Lock size={12} className="text-red-500" />}
                    </div>
                    <div className="text-[12px] font-semibold mt-0.5">{s.title}</div>
                    <div className="text-[10px] opacity-70 mt-0.5">{s.status}{s.overdue ? " · overdue" : ""}</div>
                    {s.blockers.length > 0 && (
                      <div className="mt-1.5 border-t border-current/20 pt-1 space-y-0.5">
                        {s.blockers.map((b) => (
                          <div key={b.ref} data-testid={`cp-blocker-${s.ref}-${b.ref}`} className="text-[10px] text-red-700 leading-tight">
                            <Lock size={9} className="inline mr-0.5" />{b.ref}: {b.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {i < steps.length - 1 && <div className="flex items-center text-zinc-300"><ChevronRight size={16} /></div>}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
